import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { tabbable } from 'tabbable';
import { XIcon } from '@heroicons/react/solid';
import useFocusTrap from '../../../hooks/useFocusTrap';
import * as styles from './Modal.styles';
import { ModalContext } from './ModalContext';

const Modal = ({
  children,
  isOpen,
  onClose,
  ariaLabel,
  initialFocus,
  type = 'default',
}) => {
  const closeButton = useRef();
  const modalBodyRef = useRef();
  const [isMounted, setIsMounted] = useState(false);

  // Find the last tabbable item within the modal body.
  const getLastTabbableItem = () => {
    if (!modalBodyRef.current) return null;
    const focusableItems = tabbable(modalBodyRef.current);
    const lastTabbableItem = focusableItems.length
      ? focusableItems[focusableItems.length - 1]
      : closeButton.current;
    return lastTabbableItem;
  };

  // Mimick the structure of a React ref so it works with UseFocusTrap hook.
  const [lastTabbableRef, setLastTabbableRef] = useState({
    current: getLastTabbableItem(),
  });
  const [updateModal, setUpdateModal] = useState(undefined);
  const contextValue = { updateModal, setUpdateModal };

  // Update focus trap when child content changes.
  useEffect(() => {
    setLastTabbableRef({ current: getLastTabbableItem() });
    setUpdateModal(false);
  }, [children, updateModal]);

  useFocusTrap(closeButton, lastTabbableRef, isOpen);

  const lockScroll = () => {
    const bodyElement = document.body;
    const htmlElement = document.documentElement;
    const overlay = document.querySelector('.su-modal');
    const scrollbarWidth = overlay ? `${overlay.offsetWidth - overlay.clientWidth}px` : '0px';

    htmlElement.setAttribute('style', 'overflow-y: hidden!important');

    Object.assign(bodyElement.style, {
      paddingRight: scrollbarWidth,
      overflowY: 'hidden',
      left: '0',
      right: '0',
    });
  };

  const unlockScroll = () => {
    const bodyElement = document.body;
    const htmlElement = document.documentElement;

    htmlElement.setAttribute('style', 'overflow-y: visible!important');

    // Reset body styles
    Object.assign(bodyElement.style, {
      paddingRight: '',
      overflowY: '',
      left: '',
      right: '',
    });
  };

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
      // Ensure scroll is unlocked when component unmounts (e.g., during route changes)
      unlockScroll();
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.getElementById('___gatsby').setAttribute('aria-hidden', 'true');
      lockScroll();
      if (initialFocus?.current) {
        initialFocus.current.focus();
      } else {
        closeButton.current.focus();
      }
    } else if (isMounted) {
      document.getElementById('___gatsby').setAttribute('aria-hidden', 'false');
      unlockScroll();
    }

    return () => {
      document.getElementById('___gatsby')?.setAttribute('aria-hidden', 'false');
      unlockScroll();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMounted]);

  return (
    <ModalContext.Provider value={contextValue}>
      {isOpen
        && createPortal(
          <div
            className={styles.root({ isOpen, type })}
            aria-label={ariaLabel}
            aria-hidden={!isOpen}
            aria-modal={isOpen}
            role="dialog"
            tabIndex="-1"
          >
            <div className={styles.wrapper({ type })}>
              <div className={styles.closeButtonWrapper({ type })}>
                <button
                  type="button"
                  ref={closeButton}
                  onClick={onClose}
                  className={styles.closeButton({ type })}
                  aria-label="Close modal"
                >
                  <XIcon className={styles.closeIcon({ type })} aria-hidden />
                </button>
              </div>
              <div ref={modalBodyRef}>{children}</div>
            </div>
          </div>,
          document.body,
        )}
    </ModalContext.Provider>
  );
};

export default Modal;
